import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import { holdCanvasRedrawBatchFor } from 'pages/ahu-board/three/canvasRedrawScheduler';
import type {
  IAhuBoardUnit,
  IAhuBoardTunnel,
  IAhuBoardSection,
  IAhuComponentRow,
  IAhuDmountRow,
  IAhuCoilDetail,
  IAhuFanDetail,
  IAhuAccessoryRow,
  IAhuAccessoryDetail,
  IAhuPositionRow,
  IAhuIntWallRow,
  IAhuTunnelLugPlacement,
  IAhuRatedCoil,
  IAhuEvapPackDetail,
  IAhuFloorDetail,
  IAhuUdcDetail,
  IAhuGasHeaterDetail,
  IConstructionClass,
  ICmsAhuDoc,
  IAhuListRow,
  IAhuBoardProject,
  ITunnelValidation,
  IAllPermissions,
  IBoardUser,
  IWorkflowTask,
  ITaskStatus,
  ITimelineRow,
} from 'models';

/**
 * RTK Query api for the AHU Board (Phase 6). Sub-phase 6a scope: the BOOTSTRAP
 * slice of PHASE6_AHUBOARD_INVENTORY.md §20 (every endpoint fired on board
 * load, §20.1) plus the catalogs the page shell consumes — NOT the full §20
 * catalog of 118 rows. Structured so 6b–6e extend with their own endpoints
 * (mutations land with the surfaces that own their §19.2 cascades).
 *
 * Conventions (same as projectBoardApi): shared `baseQuery` (CSRF +
 * X-Requested-With + 401→/login), full /api/v1.0/... URLs per endpoint, NOT
 * re-exported from the app/services barrel (hook-name collisions). The legacy
 * fetch-dedup idiom of §21.6 (module-level deferred guards) is free here —
 * RTKQ deduplicates concurrent identical queries.
 *
 * §21.6 forever-caches replicated only where the inventory says so:
 * construction classes (+ roof params when they land in 6e) get a session-long
 * keepUnusedDataFor; everything else uses the default and refetches on
 * invalidation ([NEG]#61 — do NOT replicate the never-cleared dedup maps).
 */

/** Session-long cache (§21.6 `_isLoaded` forever-cache equivalents). */
const KEEP_FOREVER_SECONDS = 24 * 60 * 60;

/**
 * Structural dispatch type (same idea as `tunnelLength.TunnelLengthHost`):
 * satisfied by both `AppDispatch` and the dispatch RTKQ hands an
 * `onQueryStarted`, without either importing the other's store types.
 */
type SectionRefetchDispatch = (
  action: ReturnType<typeof ahuBoardApi.endpoints.getTunnelSectionList.initiate>
) => PromiseLike<unknown>;

/**
 * A tunnel W/H change silently restates every section row underneath it —
 * re-GET them, and hold the canvas pass until they land.
 *
 * `aw_width` / `aw_height` are the section's INNER airway dimensions, and they
 * are DERIVED server-side from the parent tunnel's `width`/`height` minus the
 * wall build-up (`models_section.py` — both are `@property`, not columns). So
 * a W/H PATCH changes them for every section in the tunnel while telling the
 * client nothing: no `AHUSection.save()` runs (`tunnel_viewset
 * .partial_update` only re-saves sections for `has_floor` and multi-tunnel
 * components), so there is no `ahusection` post_save and no syncdata echo for
 * `useAhuBoardSyncdata` to apply. The cached rows keep the pre-edit numbers
 * until something refetches the list — which, before this, only a page reload
 * did.
 *
 * The visible half of that gap is on the hybrid canvas. A section cube is
 * drawn from the TUNNEL's width (`render_canvas_tunnel` → `drawSection`), so
 * it resizes the instant the PATCH lands; but the end-wall opening beside a
 * back-to-back neighbour is sized from the PEER SECTION's `aw_width`
 * (`getEndWallOpening`, canvas.view3d:2986-3014). Narrow one tunnel of a pair
 * and the wider one kept computing its opening against the peer's old width,
 * so the shoulder — the part of its end wall the narrower neighbour no longer
 * covers — was not drawn; widen it back and the shoulder stayed. Both
 * corrected themselves on reload. `drawCasing`'s inner liner and the §6c2
 * accessory math read the same two fields.
 *
 * Legacy has the identical staleness (`model/tunnel.js` onChange refetches
 * accessories, never sections), so this is a deliberate dedrift, not a port.
 */
export const refetchSectionsAfterTunnelResize = (
  dispatch: SectionRefetchDispatch,
  /** The PATCH body (or the syncdata `changed` diff) — a no-op without W/H. */
  patchBody: { width?: unknown; height?: unknown },
  tunnelIds: number[]
): void => {
  if (!('width' in patchBody) && !('height' in patchBody)) return;
  tunnelIds.forEach((tunnelId) => {
    holdCanvasRedrawBatchFor(
      dispatch(
        ahuBoardApi.endpoints.getTunnelSectionList.initiate(tunnelId, {
          forceRefetch: true,
          subscribe: false,
        })
      )
    );
  });
};

/** Sibling of `SectionRefetchDispatch` for the tunnel-list re-GET. */
type TunnelListRefetchDispatch = (
  action: ReturnType<typeof ahuBoardApi.endpoints.getAhuTunnelList.initiate>
) => PromiseLike<unknown>;

/**
 * Moving one hybrid tunnel along X renumbers its BACK-TO-BACK PEERS' `position`
 * server-side — re-GET the list, and hold the canvas pass until it lands.
 *
 * `position` is not a value the client owns. `_auto_address_hybrid_peers`
 * (`base_group_orchestration.py`, on `AHUTunnels` post_save) re-assigns it
 * across the whole (row, level) group on every save so that numbering follows
 * `x_offset` ascending — smallest `x_offset` is always `position` 1. So a PATCH
 * addressed to ONE tunnel silently rewrites rows the client never asked about,
 * and it does not even report the change for the tunnel it WAS addressed to:
 * the renumber is a queryset `.update()`, while `partial_update` serialises the
 * in-memory instance, so the 200 carries the PRE-renumber `position`.
 *
 * Nothing else closes that gap — `patchAhuTunnel` merges the response into the
 * one row it addressed and there is no `ahutunnels` syncdata echo for the peers
 * — so the cached addressing stayed on the pre-edit numbering until a reload.
 *
 * The visible half is the hybrid canvas's Y axis. `getTunnelZOffset` resolves
 * the peer a tunnel's Y-Offset is measured against by `position - 1`, and the
 * `unit` that decides which side `left_aligned` / `right_aligned` falls on
 * comes from THAT peer's `airflow_direction_id`. Reverse a tunnel mid-chain and
 * its `x_offset` re-bases onto the other edge (`airflowFlipPatch`), which is
 * exactly the kind of move that reorders the group — so every aligned
 * neighbour downstream of it kept aligning against the tunnel it USED to sit
 * beside, and only a reload put it right.
 *
 * Not a legacy dedrift: `_auto_address_hybrid_peers` postdates the migration
 * cut, and the Backbone board has no port of it to be stale against.
 */
export const refetchTunnelsAfterAddressingChange = (
  dispatch: TunnelListRefetchDispatch,
  /** The PATCH body (or the syncdata `changed` diff) — a no-op without them. */
  patchBody: Record<string, unknown>,
  ahuId: number
): void => {
  // The four fields `_auto_address_hybrid_peers` sorts/groups on. Airflow
  // direction is deliberately NOT one of them: flipping it moves `position`
  // only via the re-based `x_offset` the same body carries.
  const RENUMBERING_FIELDS = ['x_offset', 'position', 'row', 'level'];
  if (!RENUMBERING_FIELDS.some((field) => field in patchBody)) return;
  holdCanvasRedrawBatchFor(
    dispatch(
      ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, {
        forceRefetch: true,
        subscribe: false,
      })
    )
  );
};

/**
 * Same gap as `refetchTunnelsAfterAddressingChange`, for `base_id`.
 *
 * A base is shared, not per-tunnel: `AHUTunnels.save` propagates `base_id` to
 * every tunnel that shares it — all of level 1, or for level > 1 the tunnels
 * on the same Z centreline (`_resync_hybrid_base_ids`, the same set the
 * serializer reports as `same_base_tunnel_ids`). The 200 carries only the row
 * that was addressed, and there is no `ahutunnels` syncdata echo for the
 * peers, so their cached `base` / `base_id` — and the rails the canvas draws
 * from them — stayed on the old base until a reload.
 */
export const refetchTunnelsAfterBaseChange = (
  dispatch: TunnelListRefetchDispatch,
  patchBody: Record<string, unknown>,
  ahuId: number
): void => {
  if (!('base_id' in patchBody)) return;
  holdCanvasRedrawBatchFor(
    dispatch(
      ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, {
        forceRefetch: true,
        subscribe: false,
      })
    )
  );
};

export const ahuBoardApi = createApi({
  reducerPath: 'ahuBoardApi',
  baseQuery,
  tagTypes: [
    'Ahu',
    'AhuTunnels',
    'AhuSections',
    'AhuComponents',
    'AhuAccessories',
    'AhuProject',
    'AhuProjectTimeline',
    'AhuList',
    'AhuPermissions',
    'AhuValidation',
    'AhuSpecialOptions',
  ],
  endpoints: (builder) => ({
    // ---- §20.1 #1 — GET users/<id> (fn['current.user']; id from body[data-user_id]) ----
    getAhuBoardUser: builder.query<IBoardUser, number>({
      query: (id) => `/api/v1.0/users/${id}`,
    }),

    // ---- §20.1 #3 — GET users/permissions/ (MainView render → allPermissions) ----
    // The endpoint returns ONE dict {"permissions": {...}} — NOT an array. The
    // legacy Backbone Collection wrapped it client-side (toJSON()[0]); mirror
    // that wrap here, same as projectBoardApi (3b fix; 6a verify finding 2).
    getAhuBoardPermissions: builder.query<IAllPermissions[], void>({
      query: () => '/api/v1.0/users/permissions/',
      transformResponse: (response: IAllPermissions | IAllPermissions[]) =>
        Array.isArray(response) ? response : [response],
      providesTags: ['AhuPermissions'],
    }),

    // ---- §20.1 #4 — GET ahu/units/<id> (fn['current.ahu.model'] → unit defaults) ----
    getAhuUnit: builder.query<IAhuBoardUnit, number>({
      query: (id) => `/api/v1.0/ahu/units/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Ahu', id }],
    }),

    // ---- §20.1 #5 — GET ahu/tunnels/?ahu_id= (ahu.tunnels.fetch in fn['current.ahu']) ----
    // Legacy comparator is 'index_id' (§21.2) — sort here so every consumer
    // sees collection order.
    getAhuTunnelList: builder.query<IAhuBoardTunnel[], number>({
      query: (ahuId) => `/api/v1.0/ahu/tunnels/?ahu_id=${ahuId}`,
      transformResponse: (rows: IAhuBoardTunnel[]) =>
        [...rows].sort((a, b) => (a.index_id ?? 0) - (b.index_id ?? 0)),
      providesTags: (_r, _e, ahuId) => [{ type: 'AhuTunnels', id: ahuId }],
    }),

    // ---- §20.1 #6 — GET ahu/sections/?tunnel_id= (fn['current.ahu.sections']) ----
    // Legacy fetches tunnels SEQUENTIALLY only as an artifact of recalc order;
    // §26.1 pins parallelization as a deliberate dedrift (lengths are summed
    // per tunnel independently). Comparator `order_rate` (§21.3).
    getTunnelSectionList: builder.query<IAhuBoardSection[], number>({
      query: (tunnelId) => `/api/v1.0/ahu/sections/?tunnel_id=${tunnelId}`,
      transformResponse: (rows: IAhuBoardSection[]) =>
        [...rows].sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0)),
      providesTags: (_r, _e, tunnelId) => [{ type: 'AhuSections', id: tunnelId }],
    }),

    // ---- §20.5 #43 — GET ahu/components/?section_id= (section.components relation) ----
    // Replaces the never-cleared dedup map of [NEG]#61 with proper tags.
    getSectionComponents: builder.query<IAhuComponentRow[], number>({
      query: (sectionId) => `/api/v1.0/ahu/components/?section_id=${sectionId}`,
      providesTags: (_r, _e, sectionId) => [{ type: 'AhuComponents', id: sectionId }],
    }),

    // ---- §20.4 #38 — GET ahu/sections/dmounts/?section_id= (dmounts collection) ----
    // G29 input: the legacy fetches these via each section's tab DMount
    // collection (tunnel.js dmountListener waits on loadingTabSection); the
    // React data layer hoists them to the bootstrap so the canvas dmount
    // dims/frames don't depend on the 6e tab mount order.
    getSectionDmounts: builder.query<IAhuDmountRow[], number>({
      query: (sectionId) => `/api/v1.0/ahu/sections/dmounts/?section_id=${sectionId}`,
      providesTags: (_r, _e, sectionId) => [{ type: 'AhuSections', id: `dmounts-${sectionId}` }],
    }),

    // ---- §20.5 #47 — GET ahu/coils/<id> (concrete coil; canvas reads duty_id) ----
    // G33 re-home: the legacy duty scan reads stateStorage
    // `component.coil.model` rows (canvas.view3d:5459) which the tab.section /
    // form layer fetches from this exact endpoint at board load.
    getAhuCoilDetail: builder.query<IAhuCoilDetail, number>({
      query: (id) => `/api/v1.0/ahu/coils/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AhuComponents', id: `coil-${id}` }],
    }),

    // ---- §20.5 #47 — GET ahu/fans/<id> (concrete fan; canvas reads orientation_id) ----
    // G33 getOrientationId re-home (stateStorage `component.fan.model`,
    // canvas.view3d:5384-5393).
    getAhuFanDetail: builder.query<IAhuFanDetail, number>({
      query: (id) => `/api/v1.0/ahu/fans/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AhuComponents', id: `fan-${id}` }],
    }),

    // ---- §20.6 #51 — GET ahu/accessories/?section_id= (section.accessories relation) ----
    getSectionAccessories: builder.query<IAhuAccessoryRow[], number>({
      query: (sectionId) => `/api/v1.0/ahu/accessories/?section_id=${sectionId}`,
      providesTags: (_r, _e, sectionId) => [{ type: 'AhuAccessories', id: sectionId }],
    }),

    // ---- §20.6 #54 — GET ahu/<plural>/<id> (concrete accessory; 6c2 boot-draw
    // feed reads the §49.6 drawOnCanvas subset; forms re-use in 6e2). ----
    getAccessoryDetail: builder.query<IAhuAccessoryDetail, { plural: string; id: number }>({
      query: ({ plural, id }) => `/api/v1.0/ahu/${plural}/${id}`,
      providesTags: (_r, _e, { plural, id }) => [{ type: 'AhuAccessories', id: `${plural}-${id}` }],
    }),

    // ---- §20.6 #59 / §51.3 — GET ahu/positions/<accessory_id> (generic
    // position; serves the on_cabinet shape when keyed by accessory id). ----
    getAccessoryPosition: builder.query<IAhuPositionRow, number>({
      query: (accessoryId) => `/api/v1.0/ahu/positions/${accessoryId}`,
      providesTags: (_r, _e, accessoryId) => [{ type: 'AhuAccessories', id: `position-${accessoryId}` }],
    }),

    // ---- §20.4 #31/#34 — per-section int-wall lists (G11 boot input).
    // `plane` stamped from the source endpoint (legacy int_wall classIDs). ----
    getSectionVerticalIntWalls: builder.query<IAhuIntWallRow[], number>({
      query: (sectionId) => `/api/v1.0/ahu/sections/vertical_internal_walls/?section_id=${sectionId}`,
      transformResponse: (rows: Array<Record<string, unknown>>) =>
        rows.map((row) => ({ ...row, plane: 'vertical' } as IAhuIntWallRow)),
      providesTags: (_r, _e, sectionId) => [{ type: 'AhuSections', id: `viw-${sectionId}` }],
    }),
    getSectionHorizontalIntWalls: builder.query<IAhuIntWallRow[], number>({
      query: (sectionId) => `/api/v1.0/ahu/sections/horizontal_internal_walls/?section_id=${sectionId}`,
      transformResponse: (rows: Array<Record<string, unknown>>) =>
        rows.map((row) => ({ ...row, plane: 'horizontal' } as IAhuIntWallRow)),
      providesTags: (_r, _e, sectionId) => [{ type: 'AhuSections', id: `hiw-${sectionId}` }],
    }),

    // ---- §20.7 #63 — GET ahu/tunnel_lugs/<tunnel_id>/ (G7-G10 LugCollection
    // re-home; legacy tab.lifting.lug fetches one per base_id tunnel at boot). ----
    getTunnelLugPlacement: builder.query<IAhuTunnelLugPlacement, number>({
      query: (tunnelId) => `/api/v1.0/ahu/tunnel_lugs/${tunnelId}/`,
      providesTags: (_r, _e, tunnelId) => [{ type: 'AhuTunnels', id: `lugs-${tunnelId}` }],
    }),

    // ---- §20.5 #50 — GET ahu/rating_mode_coils/<coil_id> (RatedCoilModel —
    // THE G31 pipe feed; the coil form fetches it at init). ----
    getAhuRatedCoil: builder.query<IAhuRatedCoil, number>({
      query: (coilId) => `/api/v1.0/ahu/rating_mode_coils/${coilId}`,
      providesTags: (_r, _e, coilId) => [{ type: 'AhuComponents', id: `rated-coil-${coilId}` }],
    }),

    // ---- §20.5 #47 — GET ahu/evap_packs/<id> (G32 feed: fill/drain/overflow
    // + the server-joined cms_fill/cms_drain/cms_overflow blocks). ----
    getAhuEvapPackDetail: builder.query<IAhuEvapPackDetail, number>({
      query: (id) => `/api/v1.0/ahu/evap_packs/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AhuComponents', id: `evap-${id}` }],
    }),

    // ---- §20.5 #47 — GET ahu/user_defined_components/<id> (getSectionNameColor
    // UDC probe — stateStorage `component.user_defined_component.model` re-home). ----
    getAhuUdcDetail: builder.query<IAhuUdcDetail, number>({
      query: (id) => `/api/v1.0/ahu/user_defined_components/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AhuComponents', id: `udc-${id}` }],
    }),

    // ---- GET ahu/gas_heaters/<id> (getSectionNameColor gas-heater probe —
    // `model`, the direct-fire-aware selection marker; see IAhuGasHeaterDetail). ----
    getAhuGasHeaterDetail: builder.query<IAhuGasHeaterDetail, number>({
      query: (id) => `/api/v1.0/ahu/gas_heaters/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AhuComponents', id: `gas-heater-${id}` }],
    }),

    // ---- GET ahu/floors/<floor_id> (model.ahu.floor) — isCube plane-1 probe
    // (`tunnel.floor.get('under_insulation_label')`, canvas.view3d:1480). ----
    getAhuFloorDetail: builder.query<IAhuFloorDetail, number>({
      query: (floorId) => `/api/v1.0/ahu/floors/${floorId}`,
      providesTags: (_r, _e, floorId) => [{ type: 'AhuTunnels', id: `floor-${floorId}` }],
    }),

    // ---- §20.1 #7 — GET cms/<mfg>/v1.0.0/ahu/?construction_class_id= (fn['current.cms']) ----
    // The big CMS doc: tunnels.indexes/configurations/airflow_directions,
    // dimensions, dmounts, base, certifications, air, split_sections…
    getCmsAhuDoc: builder.query<
      ICmsAhuDoc,
      { mfgId: number; constructionClassId: number }
    >({
      query: ({ mfgId, constructionClassId }) =>
        `/api/v1.0/cms/${mfgId}/v1.0.0/ahu/?construction_class_id=${constructionClassId}`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §20.1 #8 — GET projects/<project_id> (fn['current.project']) ----
    // NO trailing slash (model.project url). The legacy fetch-success
    // document.title side effect (parity 197) is applied by the page, not here.
    getAhuBoardProject: builder.query<IAhuBoardProject, number>({
      query: (id) => `/api/v1.0/projects/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AhuProject', id }],
    }),

    // ---- §20.1 #9 — GET cms/1/v1.0.0/ahu/construction_class/ ----
    // HOISTED to bootstrap per Part-3 risk R15: legacy fetches this from
    // tab.ahu's render and stamps `ahu.constructionClass`, which the canvas
    // reads for the under-insulation Y offset — a mount-order coupling React
    // must not reproduce. Session-long cache (§21.6 `_isLoaded`).
    getConstructionClasses: builder.query<IConstructionClass[], void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/construction_class/',
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §20.1 #10 / §20.3 #23-24 — GET ahu/tunnels/<id>/validation ----
    // Defined now so the data layer is complete for the bootstrap; NOT fired
    // in 6a (validate_all_3D is kicked by the configure-tunnel window init and
    // consumed by the error manager — both 6e surfaces; §22 validation slice
    // lands with them).
    getTunnelValidation: builder.query<
      ITunnelValidation,
      { tunnelId: number; componentsOnly?: boolean }
    >({
      query: ({ tunnelId, componentsOnly }) =>
        componentsOnly
          ? `/api/v1.0/ahu/tunnels/${tunnelId}/components/validation`
          : `/api/v1.0/ahu/tunnels/${tunnelId}/validation`,
      providesTags: (_r, _e, { tunnelId }) => [{ type: 'AhuValidation', id: tunnelId }],
    }),

    // ---- §20.2 #13 — GET ahu/units/?ahu_list=true&project_id= (ViewProjectName) ----
    getAhuSiblingList: builder.query<IAhuListRow[], number>({
      query: (projectId) => `/api/v1.0/ahu/units/?ahu_list=true&project_id=${projectId}`,
      providesTags: ['AhuList'],
    }),

    // ---- ViewProjectName "Create New..." — POST ahu/units/ (new_ahu.js:73-82,
    // the `fromAhuBoard` branch: {project_id, tag, qty: 1}). Legacy re-renders
    // the dropdown from its own in-memory push (addNewAHU); invalidating
    // 'AhuList' refetches §20.2 #13 for the same result. ----
    createSiblingAhu: builder.mutation<IAhuListRow, { project_id: number; tag: string }>({
      query: (body) => ({
        url: '/api/v1.0/ahu/units/',
        method: 'POST',
        body: { ...body, qty: 1 },
      }),
      invalidatesTags: ['AhuList'],
    }),

    // ---- §20.9 #95 — GET cms/1/v1.0.0/workflow/tasks/ (ViewProjectStatus) ----
    getAhuCmsWorkflowTasks: builder.query<IWorkflowTask[], void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/workflow/tasks/',
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §20.9 #94 — GET master/v1.0.0/ahu/task_statuses/ (ViewProjectStatus) ----
    getAhuTaskStatuses: builder.query<ITaskStatus[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/task_statuses/',
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §20.9 #96 — GET projects/<id>/timeline/ (ViewProjectStatus; [NEG]#40:
    // status does NOT live-update on project change).
    //
    // Tagged so the shared Workflow Manager popup can refresh the status chip
    // after its saves land — the AHU board's legacy view re-rendered from a
    // stale collection (see ProjectStatus.tsx), a dedrift we fix rather than
    // replicate. ----
    getAhuProjectTimeline: builder.query<ITimelineRow[], number>({
      query: (projectId) => `/api/v1.0/projects/${projectId}/timeline/`,
      providesTags: (_r, _e, projectId) => [{ type: 'AhuProjectTimeline', id: projectId }],
    }),

    // ---- 6d — GET cms/1/v1.0.0/ahu/adp_gages/params/ (fn['current.cms.adp_gage'],
    // canvas.view3d:23/:56-58): ADP-gage manufacturer names for the canvas
    // hover tooltip (§3.2.7). Legacy BLOCKS the whole canvas boot on this
    // fetch; React fetches in parallel and tolerates a late map (deliberate
    // dedrift — only the tooltip's mfg text is affected pre-resolve). ----
    getAdpGageCmsParams: builder.query<
      { adp_gage?: { manufacturer?: Array<{ id: number; name: string }> } },
      void
    >({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/adp_gages/params/',
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- 6d — §20.3 #21 PATCH ahu/tunnels/<id> (no trailing slash —
    // Backbone urlRoot '/api/v1.0/ahu/tunnels/'). 6d caller: the airflow-arrow
    // raycast click → toggle_airflow_direction (model/tunnel.js:203-230,
    // `{wait:true, patch:true}` ≡ resolve-on-server-response). The success
    // cache write replaces the in-place Backbone model set so the canvas
    // re-render reads the flipped airflow_direction_id. ----
    patchAhuTunnel: builder.mutation<
      IAhuBoardTunnel,
      { id: number; ahuId: number; data: Partial<IAhuBoardTunnel> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/tunnels/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ id, ahuId, data: body }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ahuBoardApi.util.updateQueryData('getAhuTunnelList', ahuId, (rows) =>
              rows.map((row) => (row.id === data.id ? { ...row, ...data } : row))
            )
          );
          refetchSectionsAfterTunnelResize(dispatch, body, [id]);
          refetchTunnelsAfterAddressingChange(dispatch, body, ahuId);
          refetchTunnelsAfterBaseChange(dispatch, body, ahuId);
        } catch {
          /* save failed — no cache write (legacy leaves the model untouched on fail) */
        }
      },
    }),

    // ---- Hybrid AHU: "Multiple Sections" bulk Height/Width edit ----
    // POST /api/v1.0/ahu/tunnels/bulk_update/ — resolves the selected
    // sections to their distinct parent tunnels and PATCHes each one
    // server-side (AHUTunnelBulkUpdateView), replicating the same
    // recomputation a single-tunnel PATCH gets. Broad tag invalidation
    // rather than an optimistic patch: the affected tunnel ids aren't known
    // client-side ahead of the response (sections → tunnels resolution
    // happens server-side).
    bulkUpdateAhuTunnels: builder.mutation<
      unknown,
      { ahuId: number; sectionIds: number[]; height?: number; width?: number }
    >({
      query: ({ sectionIds, height, width }) => ({
        url: '/api/v1.0/ahu/tunnels/bulk_update/',
        method: 'POST',
        body: {
          section_ids: sectionIds,
          ...(height !== undefined ? { height } : {}),
          ...(width !== undefined ? { width } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { ahuId }) => [{ type: 'AhuTunnels', id: ahuId }],
    }),

    // ---- Hybrid AHU: sizer popup bulk apply ----
    // POST /api/v1.0/ahu/tunnels/sizer_apply/ — applies a sizer selection to
    // every back-to-back peer sharing a (row, level) in one request/one
    // transaction (AHUTunnelViewSet.sizer_apply_bulk), since the hybrid
    // sizer popup only shows the position=1 tunnel of each group.
    sizerApplyBulkAhuTunnels: builder.mutation<
      unknown,
      { ahuId: number; tunnels: Array<{ tunnel_id: number; [field: string]: unknown }> }
    >({
      query: ({ tunnels }) => ({
        url: '/api/v1.0/ahu/tunnels/sizer_apply/',
        method: 'POST',
        body: { tunnels },
      }),
      invalidatesTags: (_r, _e, { ahuId }) => [{ type: 'AhuTunnels', id: ahuId }],
    }),

    // ---- Hybrid AHU: "Align All Sections in X Direction" (toolbar §8.6) ----
    // POST /api/v1.0/ahu/units/<id>/align_tunnels_x/ — the server walks the
    // resolved layout out from the (row 1, level 1, position 1) anchor and
    // snaps every other tunnel along X so overlapping runs butt and gaps
    // close, then writes the resulting `x_offset`s inside one
    // `orchestration_bypass()` and recomputes the base groups once
    // (AHUUnitViewSet.align_tunnels_x → tunnel_x_alignment.compute_x_alignment).
    //
    // The 200 carries only `moved_tunnel_ids` — no tunnel bodies — and the
    // write touches `x_offset`, `position` (the auto-address signal renumbers
    // the chain), `base_id` and the lug runs. No invalidatesTags here: the
    // caller (`alignHybridTunnelsX`, boardActions) sequences the re-reads so
    // the canvas redraws once, off state that is already final.
    alignAhuTunnelsX: builder.mutation<{ moved_tunnel_ids: number[] }, number>({
      query: (ahuId) => ({
        url: `/api/v1.0/ahu/units/${ahuId}/align_tunnels_x/`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetAhuBoardUserQuery,
  useGetAhuBoardPermissionsQuery,
  useGetAhuUnitQuery,
  useGetAhuTunnelListQuery,
  useGetTunnelSectionListQuery,
  useGetSectionComponentsQuery,
  useGetSectionDmountsQuery,
  useGetAhuCoilDetailQuery,
  useGetAhuFanDetailQuery,
  useGetSectionAccessoriesQuery,
  useGetAccessoryDetailQuery,
  useGetAccessoryPositionQuery,
  // lazy variant — the fan selector's VFD tab pulls a persisted VFD's position
  // imperatively during its _getVFD-mirror init (FanVfdTab).
  useLazyGetAccessoryPositionQuery,
  useGetSectionVerticalIntWallsQuery,
  useGetSectionHorizontalIntWallsQuery,
  useGetTunnelLugPlacementQuery,
  useGetAhuRatedCoilQuery,
  useGetAhuEvapPackDetailQuery,
  useGetAhuUdcDetailQuery,
  useGetAhuGasHeaterDetailQuery,
  useGetAhuFloorDetailQuery,
  useGetCmsAhuDocQuery,
  useGetAhuBoardProjectQuery,
  useGetConstructionClassesQuery,
  useGetTunnelValidationQuery,
  useGetAhuSiblingListQuery,
  useCreateSiblingAhuMutation,
  useGetAhuCmsWorkflowTasksQuery,
  useGetAhuTaskStatusesQuery,
  useGetAhuProjectTimelineQuery,
  useGetAdpGageCmsParamsQuery,
  usePatchAhuTunnelMutation,
  useBulkUpdateAhuTunnelsMutation,
  useSizerApplyBulkAhuTunnelsMutation,
  useAlignAhuTunnelsXMutation,
} = ahuBoardApi;
